import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";

const SUIT_MAP = { S: "♠", H: "♥", D: "♦", C: "♣" };
const RED = ["H","D"];

function Card({ code, hidden, delay = 0 }) {
  if (hidden) return <div className="card-back" style={{animation:`bj-deal .35s ease-out ${delay}ms backwards`}}/>;
  const rank = code.slice(0,-1);
  const suit = code.slice(-1);
  const red = RED.includes(suit);
  return (
    <div className="card-face" data-testid={`card-${code}`} style={{animation:`bj-deal .35s ease-out ${delay}ms backwards`}}>
      <div className={`text-xl font-bold ${red?"text-[#a40606]":"text-black"}`}>{rank}<span className="ml-0.5">{SUIT_MAP[suit]}</span></div>
      <div className={`text-3xl text-center ${red?"text-[#a40606]":"text-black"}`}>{SUIT_MAP[suit]}</div>
      <div className={`text-xl font-bold text-right rotate-180 ${red?"text-[#a40606]":"text-black"}`}>{rank}<span className="ml-0.5">{SUIT_MAP[suit]}</span></div>
    </div>
  );
}

const CHIP_VALUES_HUF = [500, 1000, 5000, 10000, 50000];
const CHIP_VALUES_EUR = [1, 5, 10, 25, 100];
const CHIP_COLORS = ["#a40606","#2d6a4f","#1a4480","#1a1a1a","#d4af37"];

function FlyingChip({ value, color, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 600);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="chip absolute" style={{color, animation:"chip-fly .6s ease-out forwards", left:"50%", top:"50%"}}>{value>=1000?`${value/1000}K`:value}</div>
  );
}

export default function Blackjack() {
  const { user, currency, lang, updateBalance } = useApp();
  const [bet, setBet] = useState(currency==="HUF"?1000:5);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chipFly, setChipFly] = useState([]);

  const chips = currency==="HUF"?CHIP_VALUES_HUF:CHIP_VALUES_EUR;
  const animateChip = (val, col) => setChipFly(prev => [...prev, {id:Date.now()+Math.random(), value:val, color:col}]);

  const addChip = (v, c) => {
    setBet(prev => Number(prev||0) + v);
    animateChip(v, c);
  };

  const start = async () => {
    if (!bet || bet<=0) return toast.error("Invalid bet");
    setLoading(true);
    try {
      const r = await api.post("/games/blackjack/start", { bet: Number(bet), currency });
      setRound(r.data);
      if (r.data.balance !== undefined) updateBalance(currency, r.data.balance);
      if (r.data.state === "blackjack") toast.success("BLACKJACK!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const act = async (action) => {
    if (!round?.round_id) return;
    setLoading(true);
    try {
      const r = await api.post("/games/blackjack/action", { round_id: round.round_id, action });
      setRound(r.data);
      if (r.data.balance !== undefined) updateBalance(currency, r.data.balance);
      if (r.data.state === "win") toast.success(t(lang,"bj.win"));
      else if (r.data.state === "lose" && action !== "insurance" && action !== "split") toast.error(t(lang,"bj.lose"));
      else if (r.data.state === "push") toast.message(t(lang,"bj.push"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const playing = round?.state === "playing";
  const finished = round && !playing;
  const curHand = round?.hands?.[round?.current_hand ?? 0];

  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl gold-text mb-6">{t(lang,"bj.title")}</h1>

      <div className="felt-table-pro p-10 min-h-[560px] relative overflow-hidden">
        <div className="table-logo-watermark">
          HAVANA
          <span className="sub">BLACKJACK · PAYS 3 : 2</span>
        </div>

        {/* Card shoe top-right */}
        <div className="absolute top-6 right-6 hidden md:block">
          <div className="card-shoe">SHOE</div>
        </div>

        {/* Dealer */}
        <div className="text-center mb-8 relative z-10">
          <div className="dealer-avatar">🎩</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">{t(lang,"bj.dealer")} {round?.d_total!==undefined && `· ${round.d_total}`}</div>
          <div className="flex justify-center gap-3 flex-wrap min-h-[110px]" data-testid="dealer-hand">
            {round?.dealer?.map((c,i)=> c==="??" ? <Card key={i} hidden delay={i*150}/> : <Card key={i} code={c} delay={i*150}/>)}
          </div>
        </div>
        {finished && (
          <div className="text-center my-6 relative z-10">
            <div className="font-serif text-3xl gold-text" data-testid="bj-result">
              {round.state==="win"?t(lang,"bj.win"):round.state==="lose"?t(lang,"bj.lose"):round.state==="push"?t(lang,"bj.push"):round.state==="blackjack"?t(lang,"bj.blackjack"):""}
            </div>
            {round.payout>0 && <div className="text-[#d4af37] mt-2">+ {fmt(round.payout, currency)}</div>}
          </div>
        )}
        {/* Player hands */}
        <div className={`grid ${round?.hands?.length>1?"grid-cols-2":"grid-cols-1"} gap-8 mt-8 relative z-10`}>
          {round?.hands?.map((h, hi)=>(
            <div key={hi} className={`text-center ${playing && hi===round.current_hand?"ring-2 ring-[#d4af37] rounded-xl py-3":""}`}>
              <div className="flex justify-center gap-3 flex-wrap min-h-[110px]" data-testid={`player-hand-${hi}`}>
                {h.cards.map((c,i)=> <Card key={i} code={c} delay={i*120}/>)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mt-3">
                {t(lang,"bj.you")} {round?.hands?.length>1?`#${hi+1}`:""} · {h.total} · {fmt(h.bet, currency)}
                {h.doubled && <span className="text-[#d4af37] ml-2">2x</span>}
              </div>
              {/* Betting circle */}
              <div className="mt-3 flex justify-center">
                <div className="betting-circle">
                  {h.bet ? <span className="text-[#d4af37] font-serif text-base">{h.bet>=1000?`${(h.bet/1000).toFixed(1)}K`:h.bet}</span> : "BET"}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Rules footer */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 text-[9px] uppercase tracking-widest text-white/50 z-10">
          <span className="result-marquee">Blackjack 3:2</span>
          <span className="result-marquee">Dealer stands on 17</span>
          <span className="result-marquee">Insurance 2:1</span>
        </div>
      </div>

      {/* Flying chips overlay */}
      <div className="relative h-0">
        {chipFly.map(c => (
          <FlyingChip key={c.id} {...c} onDone={()=>setChipFly(p=>p.filter(x=>x.id!==c.id))}/>
        ))}
      </div>

      {/* Controls */}
      <div className="mt-8 lux-card p-6">
        {!round || finished ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs uppercase tracking-widest text-white/50">{t(lang,"bj.bet")} ({currency})</label>
              <input data-testid="bj-bet-input" type="number" min="1" value={bet} onChange={e=>setBet(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-md bg-[#1a1a1a] border border-white/10 focus:border-[#d4af37] focus:outline-none"/>
            </div>
            <div className="flex gap-2 flex-wrap">
              {chips.map((v,i)=>(
                <button key={v} data-testid={`bj-chip-${v}`} type="button" onClick={()=>addChip(v, CHIP_COLORS[i%CHIP_COLORS.length])}
                  className="chip" style={{color:CHIP_COLORS[i%CHIP_COLORS.length]}}>{v>=1000?`${v/1000}K`:v}</button>
              ))}
              <button data-testid="bj-clear" type="button" onClick={()=>setBet(0)} className="btn-ghost px-4 rounded-md text-xs">×</button>
            </div>
            <button data-testid="bj-deal" onClick={start} disabled={loading} className="btn-gold px-8 py-4 rounded-md">{t(lang,"bj.deal")}</button>
          </div>
        ) : (
          <div className="flex gap-3 flex-wrap justify-center">
            <button data-testid="bj-hit" onClick={()=>act("hit")} disabled={loading} className="btn-gold px-8 py-4 rounded-md">{t(lang,"bj.hit")}</button>
            <button data-testid="bj-stand" onClick={()=>act("stand")} disabled={loading} className="btn-burgundy px-8 py-4 rounded-md">{t(lang,"bj.stand")}</button>
            <button data-testid="bj-double" onClick={()=>act("double")} disabled={loading || (curHand?.cards?.length>2)} className="btn-ghost px-8 py-4 rounded-md">{t(lang,"bj.double")}</button>
            {round?.can_split && <button data-testid="bj-split" onClick={()=>act("split")} disabled={loading} className="btn-ghost px-8 py-4 rounded-md">{t(lang,"bj.split")}</button>}
            {round?.can_insurance && <button data-testid="bj-insurance" onClick={()=>act("insurance")} disabled={loading} className="btn-ghost px-8 py-4 rounded-md">{t(lang,"bj.insurance")}</button>}
          </div>
        )}
      </div>
    </div>
  );
}
